import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Heart, ArrowLeft, ZoomIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getProduct, addToCart } from '@/lib/api';
import { formatPrice, getImageUrl } from '@/lib/utils';
import { toast } from 'sonner';
import ImageZoomModal from '@/components/ImageZoomModal';
import { SEO } from '@/components/SEO';

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(0);

  const handleImageClick = (index) => {
    setZoomIndex(index);
    setZoomOpen(true);
  };

  useEffect(() => {
    getProduct(id)
      .then(res => setProduct(res.data))
      .catch(err => toast.error('Failed to load product'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAddToCart = async () => {
    try {
      await addToCart({ product_id: id, quantity });
      toast.success('Added to cart');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to add items to cart');
      } else {
        toast.error('Failed to add to cart');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="aspect-square skeleton rounded-lg" />
            <div className="space-y-4">
              <div className="h-8 skeleton rounded w-3/4" />
              <div className="h-6 skeleton rounded w-1/2" />
              <div className="h-20 skeleton rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-32 pb-16 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-medium mb-4">Product not found</h2>
          <Link to="/shop">
            <Button>Back to Shop</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isSoldOut = product.inventory === 0;

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="product-detail-page">
      <SEO 
        title={product.name}
        description={product.description?.substring(0, 160) || `Shop ${product.name} from the ${product.collection} collection at Twenty Tulips Diaries.`}
        pathname={`/product/${id}`}
        image={product.images?.[0]}
        type="product"
        product={product}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <Link
            to="/shop"
            className="inline-flex items-center text-sm text-stone-600 hover:text-stone-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Shop
          </Link>
        </nav>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {/* Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div 
              className="relative aspect-[3/4] w-full rounded-lg overflow-hidden bg-stone-100 group cursor-zoom-in"
              onClick={() => handleImageClick(zoomIndex)}
            >
              <img
                src={getImageUrl(product.images[zoomIndex] || product.images[0])}
                alt={`${product.name} - ${zoomIndex + 1}`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                data-testid={`product-image-${zoomIndex}`}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                  <ZoomIn className="h-6 w-6 text-stone-700" />
                </div>
              </div>
            </div>
            
            {/* Thumbnail Strip */}
            {product.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    className={`flex-shrink-0 w-16 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      index === zoomIndex 
                        ? 'border-stone-900 opacity-100' 
                        : 'border-stone-200 opacity-70 hover:opacity-100'
                    }`}
                    onClick={() => setZoomIndex(index)}
                    data-testid={`product-thumbnail-${index}`}
                  >
                    <img
                      src={getImageUrl(image)}
                      alt={`${product.name} thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Image Zoom Modal */}
          <ImageZoomModal
            images={product.images}
            initialIndex={zoomIndex}
            isOpen={zoomOpen}
            onClose={() => setZoomOpen(false)}
            productName={product.name}
          />

          {/* Product Info */}
          <div className="flex flex-col" data-testid="product-info">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">
                {product.collection}
              </p>
              <h1 className="text-3xl md:text-4xl font-light tracking-tight mb-4">
                {product.name}
              </h1>

              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-2xl font-light" data-testid="product-price">
                  {formatPrice(product.price)}
                </span>
                {product.compare_at_price && (
                  <span className="text-xl text-stone-400 line-through">
                    {formatPrice(product.compare_at_price)}
                  </span>
                )}
              </div>

              {/* Status Badges */}
              <div className="flex gap-2 mb-6">
                {isSoldOut && (
                  <Badge variant="destructive">Sold Out</Badge>
                )}
                {product.featured && (
                  <Badge className="bg-stone-900 text-white">Featured</Badge>
                )}
              </div>

              <p className="text-base text-stone-700 leading-relaxed mb-8">
                {product.description}
              </p>

              {/* Quantity Selector */}
              <div className="mb-8">
                <label className="text-sm uppercase tracking-widest text-stone-900 mb-3 block">
                  Quantity
                </label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={isSoldOut}
                  >
                    -
                  </Button>
                  <span className="w-12 text-center" data-testid="quantity">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.min(product.inventory, quantity + 1))}
                    disabled={isSoldOut}
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Add to Cart */}
              <div className="flex gap-4">
                <Button
                  onClick={handleAddToCart}
                  disabled={isSoldOut}
                  className="flex-1 btn-primary"
                  size="lg"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  {isSoldOut ? 'Sold Out' : 'Add to Cart'}
                </Button>
                <Button variant="outline" size="icon" className="rounded-full h-12 w-12">
                  <Heart className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Product Details */}
            <div className="mt-12 border-t border-stone-200 pt-8">
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="w-full bg-stone-200">
                  <TabsTrigger value="details" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-stone-700">Details</TabsTrigger>
                  <TabsTrigger value="care" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-stone-700">Care</TabsTrigger>
                </TabsList>
                <TabsContent value="details" className="pt-6">
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm text-stone-500">Material</dt>
                      <dd className="text-base text-stone-900">{product.material}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-stone-500">Collection</dt>
                      <dd className="text-base text-stone-900">{product.collection}</dd>
                    </div>
                  </dl>
                </TabsContent>
                <TabsContent value="care" className="pt-6">
                  <p className="text-base text-stone-700 leading-relaxed">
                    {product.care_instructions}
                  </p>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
